import {
  EscHotKeyIcon,
  ReturnHotKeyIcon,
} from '@modules/common/components/HotKeyIcons';
import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { useHotkey } from '@modules/common/hooks';
import { trackDashboardEvent } from '@modules/dashboard/helpers';
import { useGetTemplates } from '@modules/dashboard/hooks';
import { useUpdateDashboard } from '@modules/dashboard/hooks/useUpdateDashboard';
import { EditableDashboard } from '@modules/dashboard/types';
import CloseIcon from '@mui/icons-material/Close';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { DashboardUseCase } from 'generated/graphql';

interface RemoveAsTemplateDialogContentProps {
  dashboard: Pick<
    EditableDashboard,
    'id' | 'editToken' | 'viewToken' | 'version'
  >;
  skipRefetchTemplateList?: boolean;
}

export const RemoveAsTemplateDialogContent = ({
  dashboard,
  skipRefetchTemplateList = false,
}: RemoveAsTemplateDialogContentProps) => {
  const { showErrorNotification, showSuccessNotification } =
    useNotificationContext();

  const { updateDashboard, loading: updateDashboardInProgress } =
    useUpdateDashboard();

  const { refetch: refetchTemplateList, loading: fetchingTemplatesInProgress } =
    useGetTemplates({
      skip: true, // don't fetch templates when modal is open just get the refetch and loading values
    });

  const isLoading = updateDashboardInProgress || fetchingTemplatesInProgress;

  const { closeModal } = useMuiModalContext();

  const onClose = () => closeModal();

  const removeAsTemplate = async () => {
    try {
      await updateDashboard({
        updateRequest: {
          id: dashboard.id,
          viewToken: dashboard.viewToken,
          editToken: dashboard.editToken,
          useCase: DashboardUseCase.Default,
          version: dashboard.version,
        },
      });

      if (!skipRefetchTemplateList) {
        await refetchTemplateList();
      }

      showSuccessNotification({
        contentComponent: 'Dashboard has been removed as Template.',
        props: {},
      });
      onClose();
      trackDashboardEvent({
        eventName: 'Dashboard removed as Template',
        dashboardLink: window.location.href,
      });
    } catch {
      showErrorNotification({
        contentComponent: 'Something went wrong!',
        props: {},
      });
    }
  };

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    removeAsTemplate();
  };

  // TODO: Debug why form is not submitting on enter, and then remove this
  useHotkey({
    hotkey: 'Enter',
    onHotkey: () => removeAsTemplate(),
    isHotKeyDisabled: isLoading,
    actionTitle: 'Remove As Template',
  });

  return (
    <>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="h6">Remove from Templates?</Typography>
          <IconButton aria-label="close" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <Divider />
      <Box component="form" onSubmit={onSubmit}>
        <DialogContent dividers>
          <Typography variant="body1">
            Are you sure you want to remove this dashboard from templates?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Stack direction="row" m={1} spacing={2}>
            <Button
              onClick={onClose}
              variant="outlined"
              endIcon={<EscHotKeyIcon />}
            >
              No, keep it
            </Button>
            <LoadingButton
              loading={isLoading}
              type="submit"
              variant="contained"
              color="error"
              endIcon={
                <ReturnHotKeyIcon
                  isDisabled={isLoading}
                  backgroundColor="error.light"
                />
              }
            >
              Yes, Remove
            </LoadingButton>
          </Stack>
        </DialogActions>
      </Box>
    </>
  );
};
